import { cn } from "@qn/ui";
import {
  BarChart3,
  BookOpen,
  Bot,
  Brain,
  Briefcase,
  Cloud,
  Code2,
  FileText,
  FlaskConical,
  Folder,
  GitBranch,
  Globe,
  type LucideIcon,
  Map as MapIcon,
  MessageSquare,
  PenLine,
  Plug,
  Presentation,
  Receipt,
  Ruler,
  Search,
  ShieldCheck,
  Sparkles,
  Target,
  TrendingUp,
  Wrench,
  Zap,
} from "lucide-react";

/** Icons agents, projects and connectors can use. Stored by name (older rows hold an emoji; those are mapped). */
export const ICON_SET: Record<string, LucideIcon> = {
  bot: Bot,
  sparkles: Sparkles,
  code: Code2,
  search: Search,
  chart: BarChart3,
  deck: Presentation,
  pen: PenLine,
  map: MapIcon,
  folder: Folder,
  brain: Brain,
  trending: TrendingUp,
  receipt: Receipt,
  wrench: Wrench,
  book: BookOpen,
  target: Target,
  flask: FlaskConical,
  globe: Globe,
  briefcase: Briefcase,
  github: GitBranch,
  cloud: Cloud,
  zap: Zap,
  file: FileText,
  chat: MessageSquare,
  ruler: Ruler,
  shield: ShieldCheck,
  plug: Plug,
};

const LEGACY_EMOJI: Record<string, string> = {
  "🤖": "bot",
  "✨": "sparkles",
  "💻": "code",
  "🔎": "search",
  "📊": "chart",
  "🎞️": "deck",
  "✍️": "pen",
  "🗺️": "map",
  "📁": "folder",
  "🧠": "brain",
  "📈": "trending",
  "🧾": "receipt",
  "🛠️": "wrench",
  "📚": "book",
  "🎯": "target",
  "🧪": "flask",
  "🌐": "globe",
  "🧑‍💼": "briefcase",
  "🐙": "github",
  "☁️": "cloud",
  "⚡": "zap",
  "📝": "file",
  "💬": "chat",
  "📐": "ruler",
  "🔌": "plug",
};

export const iconFor = (name: string | null | undefined, fallback = "bot"): LucideIcon =>
  ICON_SET[name ?? ""] ?? ICON_SET[LEGACY_EMOJI[name ?? ""] ?? ""] ?? ICON_SET[fallback] ?? Bot;

/** A professional line icon in a soft tinted tile (replaces emoji everywhere). */
export function NamedIcon({
  name,
  fallback,
  className,
  tile = true,
}: {
  name: string | null | undefined;
  fallback?: string;
  className?: string;
  tile?: boolean;
}) {
  const I = iconFor(name, fallback);
  return tile ? (
    <span
      aria-hidden
      className={cn(
        "grid size-8 shrink-0 place-items-center rounded-lg bg-[var(--accent-soft)] text-[var(--accent-text)]",
        className,
      )}
    >
      <I className="size-[18px]" strokeWidth={1.75} />
    </span>
  ) : (
    <I aria-hidden className={cn("size-4", className)} strokeWidth={1.75} />
  );
}
